import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  CreateUpstreamAccountRequest,
  CreateUpstreamKeyBucketRequest,
  UpstreamAccountRecord,
  UpstreamKeyBucketRecord,
} from '@nadoutong/contracts';
import {
  useUpstreamAccountsList,
  useCreateUpstreamAccount,
  useUpdateUpstreamAccount,
  useDeleteUpstreamAccount,
} from '../api/useUpstreamAccounts.ts';
import {
  useKeyBucketsList,
  useCreateKeyBucket,
  useDeleteKeyBucket,
  useRevealKeyBucketSecret,
  useUpdateKeyBucket,
} from '../api/useKeyBuckets.ts';
import { useKeyChannelBindingsList } from '../api/useKeyChannelBindings.ts';
import { ChevronDown, Eye, EyeOff, FilePen, Network, Plus, Route, Trash2, Workflow } from 'lucide-react';
import { StatusChip } from './StatusChip.tsx';
import { Modal } from './Modal.tsx';
import { KeyChannelEgressSheet } from './KeyChannelEgressSheet.tsx';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, IconButton, Input } from '../ui/index.ts';

/**
 * Supply tab content for UpstreamDetail.
 *
 * Keeps the runtime objects separate while presenting the operator workflow as
 * one workbench: supplier accounts -> key buckets -> channel / egress bindings.
 */
export function AccountsCard({
  upstreamId,
  focusAccountId = null,
  focusKeyBucketId = null,
  focusAddChannelId = null,
}: {
  readonly upstreamId: string;
  readonly focusAccountId?: string | null;
  readonly focusKeyBucketId?: string | null;
  readonly focusAddChannelId?: string | null;
}): JSX.Element {
  const t = useT();
  const { data: accounts, isLoading, isError, error } = useUpstreamAccountsList(upstreamId);
  const keyBucketsQ = useKeyBucketsList(upstreamId);
  const [addOpen, setAddOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const items = accounts ?? [];
  const allBuckets = keyBucketsQ.data ?? [];
  const bucketsByAccount = useMemo(() => {
    const next = new Map<string, UpstreamKeyBucketRecord[]>();
    for (const bucket of allBuckets) {
      if (bucket.account_id === null) {
        continue;
      }
      const existing = next.get(bucket.account_id);
      if (existing === undefined) {
        next.set(bucket.account_id, [bucket]);
      } else {
        existing.push(bucket);
      }
    }
    return next;
  }, [allBuckets]);
  const enabledAccountCount = items.filter((account) => account.enabled).length;
  const enabledKeyCount = allBuckets.filter((bucket) => bucket.enabled).length;

  useEffect(() => {
    if (focusAccountId !== null && focusAccountId.length > 0) {
      setExpandedId(focusAccountId);
    }
  }, [focusAccountId]);

  if (isLoading || keyBucketsQ.isLoading) {
    return (
      <div className="rounded-lg border border-line-soft bg-card p-6">
        <div className="mb-4 h-8 w-48 animate-pulse rounded bg-raised" />
        <div className="h-16 animate-pulse rounded bg-raised" />
      </div>
    );
  }

  if (isError || keyBucketsQ.isError) {
    const message = error instanceof Error
      ? error.message
      : keyBucketsQ.error instanceof Error
        ? keyBucketsQ.error.message
        : t('common.network_error');
    return (
      <div className="rounded-lg border border-line-soft bg-card p-6" data-testid="accounts-card-error">
        <p className="m-0 text-body text-bad" role="alert">
          {message}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-line-soft bg-card" data-testid="accounts-card">
      <div className="flex flex-col gap-5 px-6 pb-4 pt-5" data-testid="supply-workbench-card">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Workflow aria-hidden="true" className="h-5 w-5 shrink-0 text-signal" />
              <h3 className="m-0 text-section text-paper">
                {t('upstream_accounts.title')}
              </h3>
            </div>
            <p className="mt-1 max-w-3xl text-dense text-paper-low">
              {t('upstream_accounts.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/supply/upstreams/${encodeURIComponent(upstreamId)}?tab=channels`}
              data-testid="supply-workbench-channels-link"
              className="inline-flex h-8 items-center gap-1.5 rounded border border-line px-3.5 text-dense font-medium text-paper-mid transition-colors duration-120 hover:border-line-strong hover:bg-card hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            >
              <Network aria-hidden="true" className="h-4 w-4" />
              <span>{t('upstream_accounts.action.channel_catalog')}</span>
            </Link>
            <Link
              to="/supply/egress-profiles"
              data-testid="supply-workbench-templates-link"
              className="inline-flex h-8 items-center gap-1.5 rounded border border-line px-3.5 text-dense font-medium text-paper-mid transition-colors duration-120 hover:border-line-strong hover:bg-card hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            >
              <Route aria-hidden="true" className="h-4 w-4" />
              <span>{t('upstream_accounts.action.template_library')}</span>
            </Link>
            <Button
              variant="primary"
              onClick={() => setAddOpen(true)}
              data-testid="accounts-add-btn"
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              <span>{t('upstream_accounts.add_btn')}</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="supply-workbench-metrics">
          <SupplyMetric
            label={t('upstream_accounts.metric.accounts')}
            value={String(items.length)}
            detail={t('upstream_accounts.metric.enabled_accounts', { count: String(enabledAccountCount) })}
          />
          <SupplyMetric
            label={t('upstream_accounts.metric.key_buckets')}
            value={String(allBuckets.length)}
            detail={t('upstream_accounts.metric.enabled_keys', { count: String(enabledKeyCount) })}
          />
          <SupplyMetric
            label={t('upstream_accounts.metric.disabled_keys')}
            value={String(Math.max(0, allBuckets.length - enabledKeyCount))}
            detail={t('upstream_accounts.metric.disabled_key_detail')}
          />
          <SupplyMetric
            label={t('upstream_accounts.metric.workbench')}
            value={t('upstream_accounts.metric.workbench_value')}
            detail={t('upstream_accounts.metric.workbench_detail')}
          />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="px-6 pb-6 text-body text-paper-low" data-testid="accounts-empty">
          {t('upstream_accounts.empty')}
        </div>
      ) : (
        <ul className="flex flex-col gap-3 px-6 pb-6" data-testid="accounts-list">
          {items.map((acct) => (
            <AccountRow
              key={acct.id}
              account={acct}
              upstreamId={upstreamId}
              expanded={expandedId === acct.id}
              focusKeyBucketId={expandedId === acct.id && focusAccountId === acct.id ? focusKeyBucketId : null}
              focusAddChannelId={expandedId === acct.id && focusAccountId === acct.id ? focusAddChannelId : null}
              buckets={bucketsByAccount.get(acct.id) ?? []}
              onToggleExpand={() =>
                setExpandedId(expandedId === acct.id ? null : acct.id)
              }
            />
          ))}
        </ul>
      )}

      {addOpen ? (
        <CreateAccountModal
          upstreamId={upstreamId}
          onClose={() => setAddOpen(false)}
        />
      ) : null}
    </div>
  );
}

function SupplyMetric({
  label,
  value,
  detail,
}: {
  readonly label: string;
  readonly value: string;
  readonly detail: string;
}): JSX.Element {
  return (
    <div className="rounded-lg bg-well px-3 py-2">
      <span className="block text-label text-paper-low">
        {label}
      </span>
      <span className="mt-1 block truncate font-mono text-stat text-paper">
        {value}
      </span>
      <span className="mt-0.5 block truncate text-label text-paper-mid">
        {detail}
      </span>
    </div>
  );
}

// ─── Account row (expandable) ──────────────────────────────────────────

interface AccountRowProps {
  readonly account: UpstreamAccountRecord;
  readonly upstreamId: string;
  readonly expanded: boolean;
  readonly focusKeyBucketId: string | null;
  readonly focusAddChannelId: string | null;
  readonly buckets: readonly UpstreamKeyBucketRecord[];
  readonly onToggleExpand: () => void;
}

function AccountRow({
  account,
  upstreamId,
  expanded,
  focusKeyBucketId,
  focusAddChannelId,
  buckets,
  onToggleExpand,
}: AccountRowProps): JSX.Element {
  const t = useT();
  const updateMut = useUpdateUpstreamAccount(upstreamId);
  const deleteMut = useDeleteUpstreamAccount(upstreamId);
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const enabledKeyCount = buckets.filter((bucket) => bucket.enabled).length;

  function handleToggleEnabled(): void {
    updateMut.mutate(
      { accountId: account.id, body: { enabled: !account.enabled } },
      {
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  function handleDelete(): void {
    deleteMut.mutate(account.id, {
      onSuccess: () => {
        toast.success(t('upstream_accounts.toast.deleted'));
        setConfirmDelete(false);
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  return (
    <li
      className="flex flex-col rounded-lg border border-line-soft bg-well"
      data-testid={`account-row-${account.id}`}
    >
      {/* Header */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="flex w-full flex-col gap-3 rounded-lg px-4 py-3 text-left transition-colors duration-120 hover:bg-raised/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 sm:flex-row sm:items-center sm:justify-between"
        aria-expanded={expanded}
        data-testid={`account-toggle-${account.id}`}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="min-w-0 truncate text-body font-medium text-paper">{account.label}</span>
            <StatusChip
              variant={account.enabled ? 'ok' : 'neutral'}
              label={account.enabled ? t('upstream_accounts.chip.enabled') : t('upstream_accounts.chip.disabled')}
            />
            <span className="rounded-full bg-raised px-2 py-0.5 font-mono text-micro text-paper-mid">
              {t('upstream_accounts.keys.count', { count: String(buckets.length) })}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <AccountStat label={t('upstream_accounts.metric.rpm')} value={String(account.rpm_limit)} />
            <AccountStat label={t('upstream_accounts.metric.discount')} value={String(account.rate_multiplier)} />
            <AccountStat label={t('upstream_accounts.metric.key_buckets')} value={String(buckets.length)} />
            <AccountStat label={t('upstream_accounts.metric.enabled_keys_short')} value={String(enabledKeyCount)} />
          </div>
        </div>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 text-paper-low transition-transform duration-120 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Expanded content */}
      {expanded ? (
        <div className="flex flex-col gap-4 border-t border-line-soft px-4 py-4">
          {/* Inline editing row */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <InlineEditField
              label="RPM"
              initialValue={String(account.rpm_limit)}
              type="number"
              onSubmit={(v) => {
                const rpm = parseInt(v, 10);
                if (Number.isFinite(rpm) && rpm > 0) {
                  updateMut.mutate({ accountId: account.id, body: { rpm_limit: rpm } });
                }
              }}
            />
            <InlineEditField
              label={t('upstream_accounts.add_modal.rate_multiplier')}
              initialValue={String(account.rate_multiplier)}
              type="number"
              step="0.01"
              onSubmit={(v) => {
                const mul = parseFloat(v);
                if (Number.isFinite(mul) && mul > 0) {
                  updateMut.mutate({ accountId: account.id, body: { rate_multiplier: mul } });
                }
              }}
            />
            <div className="flex flex-col gap-2">
              <span className="text-label text-paper-mid">
                {t('upstream_accounts.chip.status_label')}
              </span>
              <Button
                size="sm"
                onClick={handleToggleEnabled}
                disabled={updateMut.isPending}
                className={account.enabled ? 'border-ok-line text-ok hover:bg-ok-dim hover:text-ok' : undefined}
              >
                {account.enabled ? t('upstream_accounts.chip.enabled') : t('upstream_accounts.chip.disabled')}
              </Button>
            </div>
          </div>

          {/* Key buckets for this account */}
          <div className="flex flex-col gap-2">
            <h4 className="m-0 text-label font-medium text-paper-mid">
              {t('upstream_accounts.section.keys')}
            </h4>
            <AccountKeyBucketsList
              upstreamId={upstreamId}
              accountId={account.id}
              buckets={buckets}
              focusKeyBucketId={focusKeyBucketId}
              focusAddChannelId={focusAddChannelId}
            />
          </div>

          {/* Delete action */}
          <div className="flex items-center justify-end gap-2 border-t border-line-soft pt-3">
            {confirmDelete ? (
              <>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={handleDelete}
                  disabled={deleteMut.isPending}
                >
                  {deleteMut.isPending ? t('common.deleting') : t('common.delete')}
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
                className="hover:bg-bad-dim hover:text-bad"
                data-testid={`account-delete-${account.id}`}
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </li>
  );
}

function AccountStat({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <span className="rounded-lg bg-raised px-2 py-1">
      <span className="block text-micro font-medium text-paper-low">
        {label}
      </span>
      <span className="block truncate font-mono text-dense font-medium text-paper-mid">
        {value}
      </span>
    </span>
  );
}

// ─── Key buckets filtered by account ───────────────────────────────────

function AccountKeyBucketsList({
  upstreamId,
  accountId,
  buckets,
  focusKeyBucketId,
  focusAddChannelId,
}: {
  readonly upstreamId: string;
  readonly accountId: string;
  readonly buckets: readonly UpstreamKeyBucketRecord[];
  readonly focusKeyBucketId: string | null;
  readonly focusAddChannelId: string | null;
}): JSX.Element {
  const t = useT();
  const [addKeyOpen, setAddKeyOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-micro text-paper-low">
          {buckets.length > 0 ? t('upstream_accounts.keys.count', { count: String(buckets.length) }) : ''}
        </span>
        <Button size="sm" onClick={() => setAddKeyOpen(true)}>
          {t('upstream_accounts.keys.add_btn')}
        </Button>
      </div>
      {buckets.length === 0 ? (
        <span className="text-dense text-paper-low">
          {t('upstream_accounts.keys.none')}
        </span>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="hidden grid-cols-[minmax(0,1.1fr)_6rem_minmax(0,1.35fr)_max-content] gap-3 px-3 text-label font-medium text-paper-low xl:grid">
            <span>{t('upstream_accounts.key_table.key')}</span>
            <span>{t('upstream_accounts.key_table.rpm')}</span>
            <span>{t('upstream_accounts.key_table.channels')}</span>
            <span>{t('upstream_accounts.key_table.actions')}</span>
          </div>
          {buckets.map((kb) => (
            <KeyBucketWithChannels
              key={kb.id}
              bucket={kb}
              upstreamId={upstreamId}
              focusAddChannelId={focusKeyBucketId === kb.id ? focusAddChannelId : null}
            />
          ))}
        </div>
      )}
      {addKeyOpen ? (
        <CreateKeyModal
          upstreamId={upstreamId}
          accountId={accountId}
          onClose={() => setAddKeyOpen(false)}
        />
      ) : null}
    </div>
  );
}

function KeyBucketWithChannels({
  bucket,
  upstreamId,
  focusAddChannelId,
}: {
  readonly bucket: UpstreamKeyBucketRecord;
  readonly upstreamId: string;
  readonly focusAddChannelId: string | null;
}): JSX.Element {
  const t = useT();
  const bindingsQ = useKeyChannelBindingsList(upstreamId, bucket.id);
  const revealMut = useRevealKeyBucketSecret(upstreamId);
  const deleteMut = useDeleteKeyBucket(upstreamId);
  const toast = useToast();
  const bindings = bindingsQ.data ?? [];
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [egressSheetOpen, setEgressSheetOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [fullSecret, setFullSecret] = useState<string | null>(null);

  useEffect(() => {
    if (focusAddChannelId !== null && focusAddChannelId.length > 0) {
      setEgressSheetOpen(true);
    }
  }, [focusAddChannelId]);

  function revealSecret(onSuccess?: () => void): void {
    revealMut.mutate(bucket.id, {
      onSuccess: (result) => {
        setFullSecret(result.auth_secret_ref);
        onSuccess?.();
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function handleToggleSecret(): void {
    if (fullSecret !== null) {
      setFullSecret(null);
      return;
    }
    revealSecret();
  }

  function handleOpenEdit(): void {
    if (fullSecret !== null) {
      setEditOpen(true);
      return;
    }
    revealSecret(() => setEditOpen(true));
  }

  return (
    <div
      className="grid gap-3 rounded-lg bg-card px-3 py-2 xl:grid-cols-[minmax(0,1.1fr)_6rem_minmax(0,1.35fr)_max-content] xl:items-center"
      data-testid={`key-bucket-row-${bucket.id}`}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 truncate text-dense font-medium text-paper">
            {bucket.label || '—'}
          </span>
          <span className={`rounded-full border px-2 py-0.5 text-micro font-medium ${bucket.enabled ? 'border-ok-line bg-ok-dim/50 text-ok' : 'border-line text-paper-low'}`}>
            {bucket.enabled ? t('key_buckets.enabled') : t('key_buckets.disabled')}
          </span>
        </div>
        <span
          className={`mt-1 block max-w-full break-all rounded bg-well px-2 py-1 font-mono text-micro ${
            fullSecret === null ? 'text-paper-low' : 'text-signal'
          }`}
          data-testid={`key-bucket-secret-${bucket.id}`}
        >
          {fullSecret ?? bucket.auth_secret_preview ?? t('upstream_accounts.keys.write_only')}
        </span>
      </div>

      <div className="flex items-center gap-2 xl:block">
        <span className="text-label font-medium text-paper-low xl:hidden">
          {t('upstream_accounts.key_table.rpm')}
        </span>
        <span className="font-mono text-dense font-medium text-paper-mid">
          {bucket.rpm_limit}
        </span>
      </div>

      <div className="min-w-0">
        <span className="mb-1 block text-label font-medium text-paper-low xl:hidden">
          {t('upstream_accounts.key_table.channels')}
        </span>
        <div className="flex min-w-0 flex-wrap gap-1.5">
          {bindingsQ.isLoading ? (
            <span className="text-micro text-paper-low">
              {t('upstream_accounts.bindings.loading')}
            </span>
          ) : bindingsQ.isError ? (
            <span className="text-micro text-bad">
              {t('common.network_error')}
            </span>
          ) : bindings.length > 0 ? (
            bindings.map((b) => (
              <span
                key={b.channel_id}
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-signal-dim px-2 py-0.5 text-micro font-medium text-signal"
              >
                <span className="max-w-[9rem] overflow-hidden text-ellipsis whitespace-nowrap">
                  {b.channel_slug}
                </span>
                <span className="max-w-[6rem] overflow-hidden text-ellipsis whitespace-nowrap font-mono text-micro text-signal-deep">
                  {b.channel_id}
                </span>
                <span className="text-signal-deep">/</span>
                <span className="max-w-[11rem] overflow-hidden text-ellipsis whitespace-nowrap text-paper-mid">
                  {b.egress_profile_name ?? t('upstream_accounts.egress.unassigned')}
                </span>
                {b.egress_config_override_json !== null ? (
                  <>
                    <span className="text-signal-deep">+</span>
                    <span className="font-mono text-micro text-paper-low">
                      {t('upstream_accounts.bindings.override')}
                    </span>
                  </>
                ) : null}
              </span>
            ))
          ) : (
            <span className="text-micro text-paper-low">
              {t('upstream_accounts.channels.none')}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1 xl:justify-end">
        <Button
          size="sm"
          variant="ghost"
          onClick={handleToggleSecret}
          disabled={revealMut.isPending}
          title={fullSecret === null ? t('upstream_accounts.keys.show_secret') : t('upstream_accounts.keys.hide_secret')}
          data-testid={`key-bucket-secret-toggle-${bucket.id}`}
        >
          {fullSecret === null ? (
            <Eye aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <EyeOff aria-hidden="true" className="h-3.5 w-3.5" />
          )}
          <span className="whitespace-nowrap">
            {fullSecret === null ? t('upstream_accounts.keys.show_secret') : t('upstream_accounts.keys.hide_secret')}
          </span>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleOpenEdit}
          disabled={revealMut.isPending}
          title={t('upstream_accounts.keys.edit_secret')}
          data-testid={`key-bucket-edit-${bucket.id}`}
        >
          <FilePen aria-hidden="true" className="h-3.5 w-3.5" />
          <span className="whitespace-nowrap">{t('upstream_accounts.keys.edit_secret')}</span>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setEgressSheetOpen(true)}
          title={t('upstream_accounts.keys.configure_channels_egress')}
        >
          <Route aria-hidden="true" className="h-3.5 w-3.5" />
          <span>{t('upstream_accounts.keys.configure_channels_egress')}</span>
        </Button>
        {confirmDelete ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                deleteMut.mutate(bucket.id, {
                  onSuccess: () => toast.success(t('upstream_accounts.keys.toast.deleted')),
                  onError: (err) => toast.error(coerceErrorMessage(err)),
                });
              }}
              disabled={deleteMut.isPending}
            >
              {t('common.delete')}
            </Button>
          </>
        ) : (
          <IconButton
            size="sm"
            label={t('common.delete')}
            onClick={() => setConfirmDelete(true)}
            className="hover:bg-bad-dim hover:text-bad"
          >
            <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
          </IconButton>
        )}
      </div>
      {editOpen && fullSecret !== null ? (
        <EditKeyModal
          upstreamId={upstreamId}
          bucket={bucket}
          initialSecret={fullSecret}
          onSecretUpdated={(nextSecret) => setFullSecret((current) => (current === null ? null : nextSecret))}
          onClose={() => setEditOpen(false)}
        />
      ) : null}
      {egressSheetOpen ? (
        <KeyChannelEgressSheet
          upstreamId={upstreamId}
          bucketId={bucket.id}
          bucketLabel={bucket.label || bucket.auth_secret_preview || bucket.id}
          currentBindings={bindings}
          initialAddChannelId={focusAddChannelId}
          onClose={() => setEgressSheetOpen(false)}
        />
      ) : null}
    </div>
  );
}

// ─── Inline edit field ─────────────────────────────────────────────────

function InlineEditField({
  label,
  initialValue,
  type,
  step,
  onSubmit,
}: {
  readonly label: string;
  readonly initialValue: string;
  readonly type: 'text' | 'number';
  readonly step?: string;
  readonly onSubmit: (value: string) => void;
}): JSX.Element {
  const [value, setValue] = useState(initialValue);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setValue(initialValue);
    setDirty(false);
  }, [initialValue]);

  function commit(): void {
    if (!dirty) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setValue(initialValue);
      setDirty(false);
      return;
    }
    onSubmit(trimmed);
    setDirty(false);
  }

  return (
    <label className="flex flex-col gap-2">
      <span className="text-label text-paper-mid">
        {label}
      </span>
      <Input
        type={type}
        step={step}
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          setDirty(true);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        mono
      />
    </label>
  );
}

// ─── Edit key modal ───────────────────────────────────────────────────

function EditKeyModal({
  upstreamId,
  bucket,
  initialSecret,
  onSecretUpdated,
  onClose,
}: {
  readonly upstreamId: string;
  readonly bucket: UpstreamKeyBucketRecord;
  readonly initialSecret: string;
  readonly onSecretUpdated: (nextSecret: string) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const updateMut = useUpdateKeyBucket(upstreamId);
  const toast = useToast();
  const [enabled, setEnabled] = useState(bucket.enabled);
  const [formError, setFormError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const fd = new FormData(e.currentTarget);
    const label = (fd.get('label') as string).trim();
    const authSecretRef = (fd.get('auth_secret_ref') as string).trim();
    const rpmRaw = (fd.get('rpm_limit') as string).trim();

    if (authSecretRef.length === 0) {
      setFormError(t('upstream_accounts.keys.add_modal.error.secret_required'));
      return;
    }

    const rpmLimit = Number.parseInt(rpmRaw, 10);
    if (!Number.isFinite(rpmLimit) || rpmLimit <= 0) {
      setFormError(t('upstream_accounts.keys.edit_modal.error.rpm_invalid'));
      return;
    }

    const body: {
      label?: string;
      auth_secret_ref?: string;
      rpm_limit?: number;
      enabled?: boolean;
    } = {};
    if (label !== bucket.label) body.label = label;
    if (authSecretRef !== initialSecret) body.auth_secret_ref = authSecretRef;
    if (rpmLimit !== bucket.rpm_limit) body.rpm_limit = rpmLimit;
    if (enabled !== bucket.enabled) body.enabled = enabled;

    if (Object.keys(body).length === 0) {
      setFormError(t('upstream_accounts.keys.edit_modal.error.no_changes'));
      return;
    }

    updateMut.mutate(
      { bucketId: bucket.id, body },
      {
        onSuccess: () => {
          if (body.auth_secret_ref !== undefined) {
            onSecretUpdated(authSecretRef);
          }
          toast.success(t('upstream_accounts.keys.toast.updated'));
          onClose();
        },
        onError: (err) => {
          const msg = coerceErrorMessage(err);
          setFormError(msg);
          toast.error(msg);
        },
      },
    );
  }

  return (
    <Modal
      isOpen
      onClose={() => { if (!updateMut.isPending) onClose(); }}
      titleId="key-edit-title"
      testId="key-edit-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="key-edit-title" className="m-0 text-title text-paper">
          {t('upstream_accounts.keys.edit_modal.title')}
        </h2>

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.keys.add_modal.auth_secret_ref')}
          </span>
          <Input
            name="auth_secret_ref"
            type="text"
            defaultValue={initialSecret}
            disabled={updateMut.isPending}
            mono
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.keys.add_modal.label')}
          </span>
          <Input
            name="label"
            type="text"
            defaultValue={bucket.label}
            disabled={updateMut.isPending}
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.keys.add_modal.rpm')}
          </span>
          <Input
            name="rpm_limit"
            type="number"
            min={1}
            defaultValue={bucket.rpm_limit}
            disabled={updateMut.isPending}
            mono
            autoComplete="off"
          />
        </label>

        <label className="flex items-center justify-between rounded-lg bg-well px-3 py-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.keys.edit_modal.enabled')}
          </span>
          <input
            type="checkbox"
            checked={enabled}
            disabled={updateMut.isPending}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
            className="h-4 w-4 accent-signal max-md:h-5 max-md:w-5"
          />
        </label>

        {formError !== null ? (
          <p className="m-0 text-label text-bad" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => { if (!updateMut.isPending) onClose(); }}
            disabled={updateMut.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={updateMut.isPending}
            data-testid="key-edit-submit"
          >
            {updateMut.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Create key modal ─────────────────────────────────────────────────

function CreateKeyModal({
  upstreamId,
  accountId,
  onClose,
}: {
  readonly upstreamId: string;
  readonly accountId: string;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const createMut = useCreateKeyBucket(upstreamId);
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const fd = new FormData(e.currentTarget);
    const authSecretRef = (fd.get('auth_secret_ref') as string).trim();
    if (authSecretRef.length === 0) {
      setFormError(t('upstream_accounts.keys.add_modal.error.secret_required'));
      return;
    }

    const label = (fd.get('label') as string).trim();

    const body: CreateUpstreamKeyBucketRequest = {
      account_id: accountId,
      auth_secret_ref: authSecretRef,
      ...(label.length > 0 ? { label } : {}),
    };

    createMut.mutate(body, {
      onSuccess: () => {
        toast.success(t('upstream_accounts.keys.toast.created'));
        onClose();
      },
      onError: (err) => {
        const msg = coerceErrorMessage(err);
        setFormError(msg);
        toast.error(msg);
      },
    });
  }

  return (
    <Modal
      isOpen
      onClose={() => { if (!createMut.isPending) onClose(); }}
      titleId="key-create-title"
      testId="key-create-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="key-create-title" className="m-0 text-title text-paper">
          {t('upstream_accounts.keys.add_modal.title')}
        </h2>

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.keys.add_modal.auth_secret_ref')}
          </span>
          <Input
            name="auth_secret_ref"
            type="text"
            placeholder={t('upstream_accounts.keys.add_modal.auth_secret_ref.placeholder')}
            disabled={createMut.isPending}
            mono
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.keys.add_modal.label')}
          </span>
          <Input
            name="label"
            type="text"
            disabled={createMut.isPending}
            autoComplete="off"
          />
        </label>

        <div className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.keys.add_modal.channels')}
          </span>
          <span className="rounded-lg bg-well px-3 py-2 text-label text-paper-low">
            {t('upstream_accounts.keys.add_modal.channels_hint')}
          </span>
        </div>

        {formError !== null ? (
          <p className="m-0 text-label text-bad" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => { if (!createMut.isPending) onClose(); }}
            disabled={createMut.isPending}
          >
            {t('upstream_accounts.add_modal.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={createMut.isPending}>
            {createMut.isPending
              ? t('upstream_accounts.keys.add_modal.creating')
              : t('upstream_accounts.keys.add_modal.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Create account modal ──────────────────────────────────────────────

function CreateAccountModal({
  upstreamId,
  onClose,
}: {
  readonly upstreamId: string;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const createMut = useCreateUpstreamAccount(upstreamId);
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const fd = new FormData(e.currentTarget);
    const label = (fd.get('label') as string).trim();
    if (label.length === 0) {
      setFormError(t('upstream_accounts.add_modal.error.label_required'));
      return;
    }

    const rpmStr = (fd.get('rpm_limit') as string).trim();
    const rateStr = (fd.get('rate_multiplier') as string).trim();
    const rpm = rpmStr.length > 0 ? parseInt(rpmStr, 10) : undefined;
    const rate = rateStr.length > 0 ? parseFloat(rateStr) : undefined;

    const body: CreateUpstreamAccountRequest = {
      label,
      ...(rpm !== undefined && Number.isFinite(rpm) ? { rpm_limit: rpm } : {}),
      ...(rate !== undefined && Number.isFinite(rate) ? { rate_multiplier: rate } : {}),
    };

    createMut.mutate(body, {
      onSuccess: () => {
        toast.success(t('upstream_accounts.toast.created'));
        onClose();
      },
      onError: (err) => {
        const msg = coerceErrorMessage(err);
        setFormError(msg);
        toast.error(msg);
      },
    });
  }

  return (
    <Modal
      isOpen
      onClose={() => { if (!createMut.isPending) onClose(); }}
      titleId="accounts-create-title"
      testId="accounts-create-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="accounts-create-title" className="m-0 text-title text-paper">
          {t('upstream_accounts.add_modal.title')}
        </h2>

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.add_modal.label')}
          </span>
          <Input
            name="label"
            type="text"
            placeholder={t('upstream_accounts.add_modal.label.placeholder')}
            disabled={createMut.isPending}
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.add_modal.rpm')}
          </span>
          <Input
            name="rpm_limit"
            type="number"
            min={1}
            defaultValue="60"
            disabled={createMut.isPending}
            mono
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.add_modal.rate_multiplier')}
          </span>
          <Input
            name="rate_multiplier"
            type="number"
            min={0}
            step="0.01"
            defaultValue="1.0"
            disabled={createMut.isPending}
            mono
            autoComplete="off"
          />
        </label>

        {formError !== null ? (
          <p className="m-0 text-label text-bad" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => { if (!createMut.isPending) onClose(); }}
            disabled={createMut.isPending}
          >
            {t('upstream_accounts.add_modal.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createMut.isPending}
            data-testid="accounts-create-submit"
          >
            {createMut.isPending
              ? t('upstream_accounts.add_modal.creating')
              : t('upstream_accounts.add_modal.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
